import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Download, 
  Printer, 
  ChevronDown, 
  ChevronRight,
  DollarSign,
  AlertTriangle,
  Clock,
  TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AgingReportDto } from '../../types';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import toast from 'react-hot-toast';

const InvoiceAgingReport: React.FC = () => {
  const navigate = useNavigate();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['invoice-aging-report', asOfDate],
    queryFn: () => apiService.getInvoiceAgingReport(asOfDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const toggleClientExpansion = (clientId: number) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedClients(newExpanded);
  };

  const getAgeBucketColor = (bucket: string) => {
    switch (bucket) {
      case 'current':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'days1to30':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'days31to60':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'days61to90':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'days90Plus':
        return 'bg-red-200 text-red-900 dark:bg-red-800 dark:text-red-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getAgeBucketLabel = (bucket: string) => {
    switch (bucket) {
      case 'current':
        return 'Current';
      case 'days1to30':
        return '1-30 Days';
      case 'days31to60':
        return '31-60 Days';
      case 'days61to90':
        return '61-90 Days';
      case 'days90Plus':
        return '90+ Days';
      default:
        return bucket;
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await apiService.exportInvoiceAgingReport(asOfDate);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_aging_report_${asOfDate.replace(/-/g, '')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV export downloaded successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewInvoice = (invoiceId: number) => {
    navigate(`/invoices/${invoiceId}`);
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error Loading Report
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          There was an error loading the aging report. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Print-only header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Invoice Aging Report</h1>
        <p className="text-gray-600">As of {new Date(asOfDate).toLocaleDateString()}</p>
        <p className="text-sm text-gray-500">Generated: {new Date().toLocaleString()}</p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 print:hidden">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              As of Date
            </label>
            <Input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              className="flex items-center space-x-2"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {/* Loading skeleton for summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
          {/* Loading skeleton for table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 print:grid-cols-5 print:gap-2">
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 print:border-gray-400 print:bg-white print:text-black">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 print:text-gray-700">Current</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 print:text-blue-800">
                    {formatCurrency(report.summary.current)}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-blue-500 print:text-blue-700" />
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 print:border-gray-400 print:bg-white print:text-black">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 print:text-gray-700">1-30 Days</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 print:text-yellow-800">
                    {formatCurrency(report.summary.days1To30)}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500 print:text-yellow-700" />
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 print:border-gray-400 print:bg-white print:text-black">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 print:text-gray-700">31-60 Days</p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 print:text-orange-800">
                    {formatCurrency(report.summary.days31To60)}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500 print:text-orange-700" />
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 print:border-gray-400 print:bg-white print:text-black">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 print:text-gray-700">61-90 Days</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400 print:text-red-800">
                    {formatCurrency(report.summary.days61To90)}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500 print:text-red-700" />
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 print:border-gray-400 print:bg-white print:text-black">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 print:text-gray-700">90+ Days</p>
                  <p className="text-2xl font-bold text-red-800 dark:text-red-300 print:text-red-900">
                    {formatCurrency(report.summary.days90Plus)}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-700 print:text-red-900" />
              </div>
            </div>
          </div>

          {/* Total Outstanding */}
          <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-6 w-6 text-accent-color-600 dark:text-accent-color-400" />
                <div>
                  <h3 className="text-lg font-semibold text-accent-color-900 dark:text-accent-color-100">
                    Total Outstanding
                  </h3>
                  <p className="text-sm text-accent-color-700 dark:text-accent-color-300">
                    As of {new Date(report.asOfDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-accent-color-900 dark:text-accent-color-100">
                  {formatCurrency(report.summary.totalOutstanding)}
                </p>
              </div>
            </div>
          </div>

          {/* Client Details Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden print:border-gray-400 print:bg-white">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 print:border-gray-400">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 print:text-black">
                Outstanding Invoices by Client
              </h3>
            </div>
            
            <div className="overflow-x-auto print:overflow-visible">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 print:divide-gray-400">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Current
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      1-30 Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      31-60 Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      61-90 Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      90+ Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total Due
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {report.byClient.map((client) => (
                    <React.Fragment key={client.clientId}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleClientExpansion(client.clientId)}
                            className="flex items-center space-x-2 text-left hover:text-accent-color-600 dark:hover:text-accent-color-400"
                          >
                            {expandedClients.has(client.clientId) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {client.clientName}
                            </span>
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.current)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.days1To30)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.days31To60)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.days61To90)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.days90Plus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(client.totalDue)}
                        </td>
                      </tr>
                      
                      {/* Expanded invoice details */}
                      {expandedClients.has(client.clientId) && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-700">
                            <div className="space-y-2">
                              {client.invoices.map((invoice) => (
                                <div
                                  key={invoice.invoiceId}
                                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                                >
                                  <div className="flex items-center space-x-4">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAgeBucketColor(invoice.ageBucket)}`}>
                                      {getAgeBucketLabel(invoice.ageBucket)}
                                    </span>
                                    <button
                                      onClick={() => handleViewInvoice(invoice.invoiceId)}
                                      className="text-sm font-medium text-accent-color-600 hover:text-accent-color-800 dark:text-accent-color-400 dark:hover:text-accent-color-300"
                                    >
                                      {invoice.invoiceNumber}
                                    </button>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                      Due: {new Date(invoice.dueDate).toLocaleDateString()}
                                    </span>
                                    {invoice.daysOverdue > 0 && (
                                      <span className="text-sm text-red-600 dark:text-red-400">
                                        ({invoice.daysOverdue} days overdue)
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {formatCurrency(invoice.amountDue)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No Data Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No outstanding invoices found for the selected date.
          </p>
        </div>
      )}
    </div>
  );
};

export default InvoiceAgingReport;
